import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Loader2, Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Save, X, Upload, Handshake,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'des9fk8et';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'frg_profile_photos';

const CATEGORIES = [
  'Technology', 'Insurance', 'Banking', 'Legal', 'Housing', 'Lifestyle', 'Other',
  // iter 144 - vetted_professional categories
  'Healthcare', 'Financial', 'Real Estate', 'Vehicle Advisory',
];

const PARTNER_TYPES = [
  { id: 'institutional_affiliate', label: 'Institutional Affiliate' },
  { id: 'vetted_professional',     label: 'Vetted Professional' },
  { id: 'pending',                 label: 'Pending' },
];

const LANGUAGE_OPTIONS = ['English', 'Spanish', 'Both', 'Other'];
const EXPAT_EXPERIENCE_OPTIONS = ['Less than 10', '10 to 50', '50 to 100', 'More than 100'];

const FILTER_TABS = [
  { id: 'all',                    label: 'All' },
  { id: 'institutional_affiliate', label: 'Institutional Affiliates' },
  { id: 'vetted_professional',    label: 'Vetted Professionals' },
  { id: 'pending',                label: 'Pending' },
];

const authHeaders = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/latest/global/all.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

const labelCls = 'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block';
const inputCls =
  'w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25';

/* ---------------------------- EDIT MODAL ---------------------------- */
const PartnerEditModal = ({ open, initial, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: '', description: '', logo_url: '', website_url: '', category: 'Technology', active: true,
    // iter 144 vetted-professional fields
    partner_type: 'institutional_affiliate',
    contact_email: '',
    photo_url: '',
    gallery_url: '',
    cities: [],
    languages: [],
    credentials: '',
    fee_structure: '',
    expat_experience: '',
  });
  const [cityInput, setCityInput] = useState('');
  const [metrics, setMetrics] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const widgetRef = useRef(null);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name || '',
      description: initial?.description || '',
      logo_url: initial?.logo_url || '',
      website_url: initial?.website_url || '',
      category: initial?.category || 'Technology',
      active: initial?.active !== false,
      partner_type: initial?.partner_type || 'institutional_affiliate',
      contact_email: initial?.contact_email || '',
      photo_url: initial?.photo_url || '',
      gallery_url: initial?.gallery_url || '',
      cities: Array.isArray(initial?.cities) ? initial.cities : [],
      languages: Array.isArray(initial?.languages) ? initial.languages : [],
      credentials: initial?.credentials || '',
      fee_structure: initial?.fee_structure || '',
      expat_experience: initial?.expat_experience || '',
    });
    setCityInput('');
    setMetrics(null);
    loadCloudinaryScript();

    // iter 144 Part C - per-partner metrics inside the edit drawer.
    // Only show for vetted_professional and only when editing (never on create).
    if (isEdit && initial?.partner_type === 'vetted_professional') {
      fetch(`${API}/api/admin/partner-metrics/${initial.id}`, {
        credentials: 'include', headers: authHeaders(),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d?.applicable) setMetrics(d); })
        .catch(() => {});
    }
  }, [open, initial, isEdit]);

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openUpload = () => {
    if (!window.cloudinary) return toast.error('Photo upload not ready.');
    setUploading(true);
    widgetRef.current = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUD_NAME,
        uploadPreset: UPLOAD_PRESET,
        sources: ['local', 'url'],
        multiple: false,
        resourceType: 'image',
        folder: 'mundalo_partners',
        maxFileSize: 5_000_000,
        cropping: false,
      },
      (err, result) => {
        if (err) { setUploading(false); return toast.error('Upload failed.'); }
        if (result?.event === 'success') set('logo_url', result.info.secure_url);
        if (result?.event === 'close' || result?.event === 'queues-end') setUploading(false);
      },
    );
    widgetRef.current.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    const isVetted = form.partner_type === 'vetted_professional';
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      logo_url: (form.logo_url || '').trim(),
      website_url: (form.website_url || '').trim(),
      category: form.category,
      active: !!form.active,
      partner_type: form.partner_type,
    };
    if (isVetted) {
      payload.contact_email = (form.contact_email || '').trim().toLowerCase();
      payload.photo_url = (form.photo_url || '').trim();
      payload.gallery_url = (form.gallery_url || '').trim();
      payload.cities = form.cities;
      payload.languages = form.languages;
      payload.credentials = (form.credentials || '').trim();
      payload.fee_structure = (form.fee_structure || '').trim();
      payload.expat_experience = form.expat_experience;
    }

    if (!payload.name || !payload.description) {
      return toast.error('Name and description are required.');
    }
    if (form.partner_type === 'institutional_affiliate' && !payload.website_url) {
      return toast.error('Institutional affiliates require a website URL.');
    }
    if (isVetted && !payload.contact_email) {
      return toast.error('Vetted professionals require a contact email.');
    }
    setSubmitting(true);
    try {
      const url = isEdit
        ? `${API}/api/admin/partners/${initial.id}`
        : `${API}/api/admin/partners`;
      const res = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(isEdit ? 'Partner updated.' : 'Partner created.');
      onSaved?.(data.partner);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1F2E]/45 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="partner-edit-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white border border-[#EFE6CF] rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-[#EFE6CF] sticky top-0 bg-white rounded-t-2xl">
          <h2 className="font-display italic text-2xl text-[#0A1F2E]">
            {isEdit ? 'Edit partner' : 'New partner'}
          </h2>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F4ECD8] flex items-center justify-center" data-testid="partner-edit-close">
            <X className="w-4 h-4 text-[#0A1F2E]" />
          </button>
        </header>
        <div className="p-5 space-y-4">
          <div>
            <label className={labelCls}>Name</label>
            <input type="text" value={form.name} onChange={(e) => set('name', e.target.value.slice(0, 80))} maxLength={80} className={inputCls} data-testid="partner-edit-name" />
          </div>
          <div>
            <label className={labelCls}>Description · max 1000 chars</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value.slice(0, 1000))}
              maxLength={1000}
              rows={5}
              className={inputCls}
              data-testid="partner-edit-description"
            />
            <p className="text-xs text-[#0A1F2E]/45 mt-1">{form.description.length}/1000</p>
          </div>
          <div>
            <label className={labelCls}>Logo · optional</label>
            <div className="flex items-center gap-3">
              {form.logo_url && (
                <img src={form.logo_url} alt="" className="w-16 h-16 rounded-lg object-contain ring-2 ring-[#EFE6CF] bg-white p-1" />
              )}
              <button type="button" onClick={openUpload} disabled={uploading} className="inline-flex items-center gap-2 bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2.5 rounded-full" data-testid="partner-edit-upload">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {form.logo_url ? 'Replace' : 'Upload'}
              </button>
              {form.logo_url && (
                <button type="button" onClick={() => set('logo_url', '')} className="text-[#B91C1C] text-xs underline">Remove</button>
              )}
            </div>
            <input type="text" value={form.logo_url} onChange={(e) => set('logo_url', e.target.value)} placeholder="or paste a logo URL" className={`${inputCls} mt-2`} data-testid="partner-edit-logo-url" />
          </div>
          <div>
            <label className={labelCls}>Website URL</label>
            <input type="text" value={form.website_url} onChange={(e) => set('website_url', e.target.value)} className={inputCls} data-testid="partner-edit-website-url" />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <select
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              className={inputCls}
              data-testid="partner-edit-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* iter 144 - partner_type selector + conditional vetted form */}
          <div>
            <label className={labelCls}>Partner type</label>
            <select
              value={form.partner_type}
              onChange={(e) => set('partner_type', e.target.value)}
              className={inputCls}
              data-testid="partner-edit-partner-type"
            >
              {PARTNER_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>

          {form.partner_type === 'vetted_professional' && (
            <div className="space-y-4 p-4 bg-[#F9F6EF] border border-[#EFE6CF] rounded-lg" data-testid="partner-edit-vetted-fields">
              <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#2CA8A0]">
                Vetted Professional Details
              </p>

              <div>
                <label className={labelCls}>Contact email · required</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set('contact_email', e.target.value)}
                  className={inputCls}
                  data-testid="partner-edit-contact-email"
                />
                <p className="text-[11px] text-[#0A1F2E]/55 mt-1">
                  Never shown to members. Used for partner inquiry notifications only.
                </p>
              </div>

              <div>
                <label className={labelCls}>Photo · used as the partner-card thumbnail in the directory</label>
                <div className="flex items-center gap-3">
                  {form.photo_url && (
                    <img src={form.photo_url} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-[#EFE6CF]" />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.cloudinary) return toast.error('Photo upload not ready.');
                      window.cloudinary.createUploadWidget(
                        {
                          cloudName: CLOUD_NAME, uploadPreset: UPLOAD_PRESET,
                          sources: ['local','url'], multiple: false, resourceType: 'image',
                          folder: 'mundalo_partners',
                        },
                        (err, r) => {
                          if (r?.event === 'success' && r?.info?.secure_url) set('photo_url', r.info.secure_url);
                        },
                      ).open();
                    }}
                    className="inline-flex items-center gap-2 bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2.5 rounded-full"
                    data-testid="partner-edit-photo-upload"
                  >
                    <Upload className="w-3.5 h-3.5" /> {form.photo_url ? 'Replace' : 'Upload'}
                  </button>
                  {form.photo_url && (
                    <button type="button" onClick={() => set('photo_url', '')} className="text-[#B91C1C] text-xs underline">Remove</button>
                  )}
                </div>
              </div>

              {/* iter 144g - third image: gallery photo shown on the public
                  profile alongside the thumbnail. */}
              <div>
                <label className={labelCls}>Gallery image · one additional photo for the public profile (optional)</label>
                <div className="flex items-center gap-3">
                  {form.gallery_url && (
                    <img src={form.gallery_url} alt="" className="w-16 h-16 rounded-lg object-cover ring-2 ring-[#EFE6CF]" />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.cloudinary) return toast.error('Photo upload not ready.');
                      window.cloudinary.createUploadWidget(
                        {
                          cloudName: CLOUD_NAME, uploadPreset: UPLOAD_PRESET,
                          sources: ['local','url'], multiple: false, resourceType: 'image',
                          folder: 'mundalo_partners',
                        },
                        (err, r) => {
                          if (r?.event === 'success' && r?.info?.secure_url) set('gallery_url', r.info.secure_url);
                        },
                      ).open();
                    }}
                    className="inline-flex items-center gap-2 bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-4 py-2.5 rounded-full"
                    data-testid="partner-edit-gallery-upload"
                  >
                    <Upload className="w-3.5 h-3.5" /> {form.gallery_url ? 'Replace' : 'Upload'}
                  </button>
                  {form.gallery_url && (
                    <button type="button" onClick={() => set('gallery_url', '')} className="text-[#B91C1C] text-xs underline">Remove</button>
                  )}
                </div>
              </div>

              <div>
                <label className={labelCls}>Cities of operation</label>
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="partner-edit-cities-tags">
                  {form.cities.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-[#EFE6CF] rounded-full text-xs text-[#0A1F2E]">
                      {c}
                      <button
                        type="button"
                        onClick={() => set('cities', form.cities.filter((x) => x !== c))}
                        className="text-[#B91C1C]"
                        aria-label={`Remove ${c}`}
                      >×</button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ',') && cityInput.trim()) {
                      e.preventDefault();
                      const v = cityInput.trim();
                      if (!form.cities.includes(v)) set('cities', [...form.cities, v]);
                      setCityInput('');
                    }
                  }}
                  placeholder="Type a city and press Enter"
                  className={inputCls}
                  data-testid="partner-edit-cities-input"
                />
              </div>

              <div>
                <label className={labelCls}>Languages spoken</label>
                <div className="flex flex-wrap gap-3" data-testid="partner-edit-languages">
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <label key={lang} className="inline-flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.languages.includes(lang)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...form.languages, lang]
                            : form.languages.filter((x) => x !== lang);
                          set('languages', next);
                        }}
                        className="w-4 h-4 accent-[#2CA8A0]"
                      />
                      <span className="text-sm text-[#0A1F2E]">{lang}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls}>Professional credentials</label>
                <textarea
                  rows={3}
                  value={form.credentials}
                  onChange={(e) => set('credentials', e.target.value.slice(0, 2000))}
                  className={inputCls}
                  placeholder="e.g. Licensed Mexican attorney, 12 years immigration experience"
                  data-testid="partner-edit-credentials"
                />
              </div>

              <div>
                <label className={labelCls}>Fee structure</label>
                <textarea
                  rows={2}
                  value={form.fee_structure}
                  onChange={(e) => set('fee_structure', e.target.value.slice(0, 1000))}
                  className={inputCls}
                  placeholder="e.g. Fixed fee consultations from $150 USD"
                  data-testid="partner-edit-fee-structure"
                />
              </div>

              <div>
                <label className={labelCls}>Expat client experience</label>
                <select
                  value={form.expat_experience}
                  onChange={(e) => set('expat_experience', e.target.value)}
                  className={inputCls}
                  data-testid="partner-edit-expat-experience"
                >
                  <option value="">Choose…</option>
                  {EXPAT_EXPERIENCE_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              {metrics && (
                <div className="mt-4 pt-4 border-t border-[#EFE6CF]" data-testid="partner-edit-metrics">
                  <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-3">
                    Conversation Metrics
                  </p>
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Introductions</dt><dd className="text-[#0A1F2E] font-medium">{metrics.total_introductions}</dd></div>
                    <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Total messages</dt><dd className="text-[#0A1F2E] font-medium">{metrics.total_messages}</dd></div>
                    <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Avg response time</dt><dd className="text-[#0A1F2E] font-medium">{metrics.avg_response_hours == null ? '-' : `${metrics.avg_response_hours}h`}</dd></div>
                    <div><dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Nudges sent</dt><dd className="text-[#0A1F2E] font-medium">{metrics.nudges_sent}</dd></div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Unanswered</dt>
                      <dd className={`font-medium ${metrics.unanswered_inquiries > 0 ? 'text-[#E8A830]' : 'text-[#0A1F2E]'}`} data-testid="metric-unanswered">{metrics.unanswered_inquiries}</dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wider text-[#5A6B73]">Unbilled</dt>
                      <dd className={`font-medium ${metrics.unbilled_introductions > 0 ? 'text-[#E8A830]' : 'text-[#0A1F2E]'}`} data-testid="metric-unbilled">{metrics.unbilled_introductions}</dd>
                    </div>
                  </dl>
                  <a
                    href={`/admin/partner-conversations?partner_id=${initial?.id}`}
                    className="inline-block mt-3 text-[12px] font-mono uppercase tracking-[0.16em] text-[#2CA8A0] hover:underline"
                    data-testid="metric-view-all"
                  >
                    View all conversations with this partner →
                  </a>
                </div>
              )}
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} className="w-4 h-4 accent-[#2CA8A0]" data-testid="partner-edit-active" />
            <span className="text-sm text-[#0A1F2E]">Active (visible on the Partners page)</span>
          </label>
        </div>
        <footer className="px-5 py-4 border-t border-[#EFE6CF] flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="font-mono uppercase text-[10px] tracking-[0.15em] bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] px-4 py-2 rounded-full">Cancel</button>
          <button type="submit" disabled={submitting} className="font-mono uppercase text-[10px] tracking-[0.15em] bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-50 text-white px-5 py-2 rounded-full inline-flex items-center gap-2" data-testid="partner-edit-save">
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}<Save className="w-3.5 h-3.5" /> Save
          </button>
        </footer>
      </form>
    </div>
  );
};

/* ---------------------------- MAIN PAGE ---------------------------- */
const AdminPartners = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  // iter 144 - partner_type filter
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/partners`, {
        credentials: 'include', headers: authHeaders(),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Could not load partners');
      setItems(d.partners || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const onToggleActive = async (p) => {
    try {
      const res = await fetch(`${API}/api/admin/partners/${p.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ active: !p.active }),
      });
      if (!res.ok) throw new Error('Toggle failed');
      const d = await res.json();
      setItems((cur) => cur.map((x) => (x.id === p.id ? d.partner : x)));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/partners/${p.id}`, {
        method: 'DELETE', credentials: 'include', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed');
      setItems((cur) => cur.filter((x) => x.id !== p.id));
      toast.success('Deleted.');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const persistOrder = async (next) => {
    setSavingOrder(true);
    try {
      const payload = {
        items: next.map((x, i) => ({ id: x.id, sort_order: i })),
      };
      const res = await fetch(`${API}/api/admin/partners/reorder`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Reorder failed');
    } catch (e) {
      toast.error(e.message);
      fetchList();
    } finally {
      setSavingOrder(false);
    }
  };

  const move = (id, dir) => {
    setItems((cur) => {
      const idx = cur.findIndex((x) => x.id === id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= cur.length) return cur;
      const next = cur.slice();
      [next[idx], next[target]] = [next[target], next[idx]];
      persistOrder(next);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="admin-partners-page">
        <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">Admin · Partners</p>
            <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-2">Partners</h1>
            <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
              Edit the affiliate partners that appear on <span className="font-mono">/portal/partners</span>. Use the up/down arrows to reorder.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setEditing(null); setModalOpen(true); }}
            className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-2.5 rounded-full self-start md:self-auto"
            data-testid="admin-partners-new-btn"
          >
            <Plus className="w-4 h-4" /> New partner
          </button>
        </header>

        {savingOrder && (
          <p className="text-xs text-[#1A5C6E] mb-3 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving order…</p>
        )}

        {/* iter 144 - partner_type filter chips */}
        <div className="inline-flex items-center bg-white border border-[#EFE6CF] rounded-full p-1 mb-5" data-testid="admin-partners-type-filter">
          {FILTER_TABS.map((t) => {
            const active = typeFilter === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTypeFilter(t.id)}
                className={`px-4 py-1.5 font-mono uppercase text-[10px] tracking-[0.16em] font-semibold rounded-full transition ${
                  active ? 'bg-[#0A1F2E] text-white' : 'text-[#5A6B73] hover:text-[#0A1F2E]'
                }`}
                data-testid={`admin-partners-filter-${t.id}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" /></div>
        )}
        {!loading && error && (
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 rounded-xl text-sm" data-testid="admin-partners-error">{error}</div>
        )}
        {!loading && !error && items.length === 0 && (
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-12 text-center" data-testid="admin-partners-empty">
            <Handshake className="w-10 h-10 text-[#1A5C6E]/40 mx-auto mb-3" />
            <p className="text-[#0A1F2E]/65">No partners yet - click “New partner”.</p>
          </div>
        )}
        {!loading && !error && items.length > 0 && (() => {
          const visibleItems = typeFilter === 'all'
            ? items
            : items.filter((p) => (p.partner_type || 'institutional_affiliate') === typeFilter);
          if (visibleItems.length === 0) {
            return (
              <div className="bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center text-sm text-[#0A1F2E]/65" data-testid="admin-partners-filter-empty">
                No partners match this filter yet.
              </div>
            );
          }
          return (
            <ul className="space-y-2" data-testid="admin-partners-list">
              {visibleItems.map((p, idx) => (
              <li
                key={p.id}
                className="bg-white border border-[#EFE6CF] rounded-2xl p-3 sm:p-4 flex items-center gap-4 transition"
                data-testid={`admin-partner-row-${p.id}`}
              >
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => move(p.id, -1)}
                    disabled={idx === 0 || savingOrder}
                    className="w-7 h-7 rounded-full border border-[#EFE6CF] text-[#0A1F2E] hover:bg-[#F4ECD8] flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                    data-testid={`admin-partner-up-${p.id}`}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(p.id, 1)}
                    disabled={idx === items.length - 1 || savingOrder}
                    className="w-7 h-7 rounded-full border border-[#EFE6CF] text-[#0A1F2E] hover:bg-[#F4ECD8] flex items-center justify-center transition disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                    data-testid={`admin-partner-down-${p.id}`}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                {p.logo_url ? (
                  <img src={p.logo_url} alt="" className="w-14 h-14 rounded-md object-contain ring-1 ring-[#EFE6CF] bg-white p-1 shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-[#F4ECD8] shrink-0 flex items-center justify-center">
                    <Handshake className="w-5 h-5 text-[#1A5C6E]/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-display italic text-lg text-[#0A1F2E] truncate">{p.name}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#1A5C6E] mt-0.5">{p.category}</p>
                  <p className="text-xs text-[#0A1F2E]/60 truncate mt-1">{p.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onToggleActive(p)}
                  className={`inline-flex items-center gap-1 font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-1.5 rounded-full transition shrink-0 ${
                    p.active
                      ? 'bg-[#2CA8A0]/15 text-[#1A5C6E] hover:bg-[#2CA8A0]/25'
                      : 'bg-[#EFE6CF] text-[#0A1F2E]/55 hover:bg-[#F4ECD8]'
                  }`}
                  title={p.active ? 'Visible' : 'Hidden'}
                  data-testid={`admin-partner-toggle-${p.id}`}
                >
                  {p.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {p.active ? 'Active' : 'Hidden'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(p); setModalOpen(true); }}
                  className="font-mono uppercase text-[10px] tracking-[0.15em] bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] px-3 py-1.5 rounded-full"
                  data-testid={`admin-partner-edit-${p.id}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(p)}
                  className="inline-flex items-center gap-1 font-mono uppercase text-[10px] tracking-[0.15em] bg-white border border-[#B91C1C]/35 hover:bg-[#B91C1C]/10 text-[#B91C1C] px-3 py-1.5 rounded-full"
                  data-testid={`admin-partner-delete-${p.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </li>
            ))}
          </ul>
          );
        })()}

        <PartnerEditModal
          open={modalOpen}
          initial={editing}
          onClose={() => setModalOpen(false)}
          onSaved={(p) => {
            setItems((cur) => {
              const idx = cur.findIndex((x) => x.id === p.id);
              if (idx >= 0) {
                const next = cur.slice();
                next[idx] = p;
                return next;
              }
              return [...cur, p];
            });
          }}
        />
      </div>
    </div>
  );
};

export default AdminPartners;
